import React, { useState, useEffect, useCallback } from 'react';
import { GenieProvider } from './GenieContext';
import api from '../api';

/**
 * AuthenticatedApp Component
 *
 * Wraps the app in GenieProvider only after confirming the user is authenticated
 * via a GET /api/user call (cookie sent automatically via withCredentials).
 *
 * Flow:
 * 1. Always calls GET /api/user on mount (even on the public "/" path)
 * 2. On "/" — renders immediately (non-blocking) and upgrades to GenieProvider if
 *    the session check succeeds. If the user has a valid session on "/" (e.g. after
 *    GCIP post-login redirect lands them on "/"), they are redirected to "/home".
 * 3. On protected paths — blocks rendering until session check resolves
 * 4. If 401 → hasSession stays false; ProtectedRoute handles the login redirect
 * 5. After tt-auth login, the browser returns to the returnUrl with the cookie
 *    already set. AuthenticatedApp re-mounts at that path, calls /api/user, and
 *    mounts GenieProvider so Navbar and all children see the authenticated context.
 */
const AuthenticatedApp = ({ children }) => {
  const [hasSession, setHasSession] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const checkSession = useCallback(async () => {
    console.log('[AUTH APP] ========== SESSION CHECK ==========');
    console.log('[AUTH APP] Current path:', window.location.pathname);

    try {
      await api.get('/api/user');
      console.log('[AUTH APP] ✅ Session valid (cookie accepted by server)');
      setHasSession(true);

      // If the user has a valid session but is on the public landing page "/",
      // redirect them to "/home". This handles the case where GCIP redirects
      // the browser back to "/" after a successful login instead of "/home".
      if (window.location.pathname === '/') {
        console.log('[AUTH APP] ✅ Session valid on "/" — redirecting to /home');
        window.location.replace('/home');
      }
    } catch (error) {
      // 401 means no valid session — ProtectedRoute will handle the redirect
      // on protected paths; public paths render normally without GenieProvider.
      if (error.response?.status !== 401) {
        console.warn('[AUTH APP] ⚠️ Session check failed (non-401):', error.message);
      } else {
        console.log('[AUTH APP] No active session (401) — rendering without GenieProvider');
      }
      setHasSession(false);
    } finally {
      setIsInitialized(true);
      console.log('[AUTH APP] ========================================');
    }
  }, []);

  // Initial session check on mount
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // On the public landing page "/" we do NOT block rendering while the session
  // check is in progress — show the landing page immediately and upgrade to
  // GenieProvider once the check resolves (if the user already has a valid session).
  const isPublicPath = window.location.pathname === '/';

  // Block rendering on protected paths until we know the session state.
  // This prevents a flash of ProtectedRoute's "Loading..." before GenieProvider mounts.
  if (!isInitialized && !isPublicPath) {
    return null;
  }

  if (!hasSession) {
    console.log('[AUTH APP] No session — rendering without GenieProvider');
    return children;
  }

  // Has a valid session — wrap with GenieProvider for all routes
  console.log('[AUTH APP] Session valid — rendering with GenieProvider');
  return (
    <GenieProvider>
      {children}
    </GenieProvider>
  );
};

export default AuthenticatedApp;